import React, { useImperativeHandle, forwardRef, useState } from 'react';
import { validateInit, validateForm } from '@assets/js/util/formValidation.js';
import { formAtt } from '@assets/js/util/commonUtil.js';
import CompFmtInput from '@components/form/fragment/comp-fmt-input.js';
import CompFmtSelectbox from '@components/form/fragment/comp-fmt-selectbox.js';
import {ApiSave} from '@assets/js/api/commonApi.js';

const CompFormMatchResult = forwardRef((props, ref) => {
    const [params, setParams] = useState({});
    const [title, setTitle] = useState('경기결과 등록/수정');
    const [dvsnParams, setDvsnParams] = useState({});
    const [wetclsParams, setWetclsParams] = useState([]);

    /****************************
     * Define required variables
    *****************************/
    const rootId = 'modalSheet';
    const validateOpt = {
        scopeEle: document.querySelector('#' + rootId),
        targetIds: ['mngCd', 'comptnCd', 'dvsnCd', 'wetclsCd', 'armType', 'plyrCd', 'rnk'],
        params: params,
    }

    /*************************
     * Define react functions
    **************************/
    // Forward ref to allow parent components to call event functions.
    useImperativeHandle(ref, () => ({
        modalToggle(p_params) {
            handleToggle();
            handleValue(p_params);
            setParams(p_params || {});
        },
    }));

    /**********************
     * Define functions
    ***********************/
    const handleValue = (p_params) => {
        const targetEle = document.querySelector('#' + rootId);

        // Handle mode-specific logic
        if (p_params && p_params.mode === 'M') {
            setTitle('경기결과 수정');
            // Modify mode - disable primary key fields
            const mngCdEle = targetEle?.querySelector('#mngCd');
            const comptnCdEle = targetEle?.querySelector('#comptnCd');
            const dvsnCdEle = targetEle?.querySelector('#dvsnCd');
            const wetclsCdEle = targetEle?.querySelector('#wetclsCd');
            const armTypeEle = targetEle?.querySelector('#armType');
            const plyrCdEle = targetEle?.querySelector('#plyrCd');

            if (mngCdEle) mngCdEle.disabled = true;
            if (comptnCdEle) comptnCdEle.disabled = true;
            if (dvsnCdEle) dvsnCdEle.disabled = true;
            if (wetclsCdEle) wetclsCdEle.disabled = true;
            if (armTypeEle) armTypeEle.disabled = true;
            if (plyrCdEle) plyrCdEle.disabled = true;

            setDvsnParams({comptnCd: p_params?.comptnCd});
            setWetclsParams({comptnCd: p_params?.comptnCd});
        } else {
            setTitle('경기결과 등록');
            // Insert mode - enable all fields for selection
            const mngCdEle = targetEle?.querySelector('#mngCd');
            const comptnCdEle = targetEle?.querySelector('#comptnCd');
            const dvsnCdEle = targetEle?.querySelector('#dvsnCd');
            const wetclsCdEle = targetEle?.querySelector('#wetclsCd');
            const armTypeEle = targetEle?.querySelector('#armType');
            const plyrCdEle = targetEle?.querySelector('#plyrCd');

            if (mngCdEle) mngCdEle.disabled = false;
            if (comptnCdEle) comptnCdEle.disabled = false;
            if (dvsnCdEle) {
                dvsnCdEle.disabled = false;
                setDvsnParams({});
            }
            if (wetclsCdEle) {
                wetclsCdEle.disabled = false;
                setWetclsParams({});
            }
            if (armTypeEle) armTypeEle.disabled = false;
            if (plyrCdEle) plyrCdEle.disabled = false;
        }
    }

    const handleToggle = () => {
        const targetEle = document.querySelector('#' + rootId);
        if (targetEle) {
            targetEle.style.display = targetEle.style.display === 'none' ? 'block' : 'none';
        }

        // Reset form inputs and validation state
        validateInit(validateOpt);

        Object.keys(formObj).forEach(key => {
            const ele = targetEle?.querySelector(`#${formObj[key].id}`);
            if (ele) { ele.value = ''; }
        });
    }

    const onSave = () => {
        // Validate form inputs before saving
        if (!validateForm(validateOpt)) { return; }
        if (params == null || params.mngCd === '' || params.comptnCd === '') { return; }

        // Create mtmkgCd from individual codes
        const mtmkgCd = `${params.comptnCd || ''}-${params.dvsnCd || ''}-${params.wetclsCd || ''}`;
        const saveParams = {
            ...params,
            mtmkgCd: mtmkgCd
        };

        // Fetch data from the server
        ApiSave('/sys/matchresultmng/save/info', saveParams).then(res => {
            if (res && res.success) {
                // Close the modal after saving
                handleToggle();
                // Search for updated data
                props.onGridSearch();
            }
        });
    }

    const onValChange = (e) => {
        const { id } = e.target;
        // Validate the specific input field
        validateForm(validateOpt, id);
    }

    const onInputChange = (e) => {
        const { id, value } = e.target;
        // Update the state with the new value only (no validation)
        setParams({ ...params, [id]: value });
    }

    // Handle competition code change - fetch related division and weight class data
    const onComptnCdChange = (e) => {
        const { id, value } = e.target;
        setParams({ ...params, [id]: value, dvsnCd: '', wetclsCd: '' });

        if (value) {
            // Fetch division and weight class options for selected competition
            setDvsnParams({comptnCd: value});
            setWetclsParams({comptnCd: value});
        } else {
            // Clear Params if no competition selected
            setDvsnParams({});
            setWetclsParams({});
        }
    }

    const onChangeMtmkg = (e) => {
        const { id, value } = e.target;
        const rootEle = document.getElementById(rootId);

        // Get competition code
        const comptnCdElement = rootEle.querySelector('#comptnCd');
        const comptnCd = comptnCdElement ? comptnCdElement.value : '';

        // Get selected option text for wetclsCd
        const wetclsCdElement = rootEle.querySelector('#wetclsCd');
        const wetclsCd = wetclsCdElement ? wetclsCdElement.value : '';

        // Get selected option text for dvsnCd
        const dvsnCdElement = rootEle.querySelector('#dvsnCd');
        const dvsnCd = dvsnCdElement ? dvsnCdElement.value : '';

        // Create mtmkgCd from individual codes
        const mtmkgCd = comptnCd && dvsnCd && wetclsCd ? `${comptnCd}-${dvsnCd}-${wetclsCd}` : '';

        setParams({ ...params, [id]: value, mtmkgCd: mtmkgCd });
    }

    /****************************
     * Define form config
    *****************************/
    // Dynamic form configuration based on mode
    const isModifyMode = params.mode === 'M';
    const formObj = {
        // Top section - Code selectors (dynamic disabled based on mode)
        comptnCd : formAtt({id: 'comptnCd', label: '대회', value: params['comptnCd'] || '', disabled: isModifyMode, required: true, classCol: 'col-12'}),
        dvsnCd   : formAtt({id: 'dvsnCd', label: '부문', value: params['dvsnCd'] || '', disabled: isModifyMode, required: true, classCol: 'col-4'}),
        wetclsCd : formAtt({id: 'wetclsCd', label: '체급', value: params['wetclsCd'] || '', disabled: isModifyMode, required: true, classCol: 'col-4'}),
        armType  : formAtt({id: 'armType', label: '팔 유형', value: params['armType'] || '', disabled: isModifyMode, required: true, classCol: 'col-4'}),

        // Player and result information
        plyrCd   : formAtt({id: 'plyrCd', label: '선수코드', value: params['plyrCd'] || '', disabled: isModifyMode, required: true, maxLength: 10, classCol: 'col-6'}),
        rnk      : formAtt({id: 'rnk', label: '등수', value: params['rnk'] || '', required: true, classCol: 'col-6'}),
    }

    return (
        <>
            <div
                className="modal modal-sheet p-1 py-md-5"
                style={{ display: 'none', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                tabIndex={-1}
                role="dialog"
                id={rootId}
            >
                <div className="modal-dialog modal-lg">
                    <div className="modal-content rounded-4 shadow">
                        <div className="modal-header border-bottom-0">
                            <h1 className="modal-title fs-5">{title}</h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={handleToggle}
                            />
                        </div>
                        <div className="modal-body py-0">
                            <div className="row g-5">
                                <div className="col-lg-12">
                                    <form className="needs-validation" noValidate="">
                                        <div className="row">
                                            {/* Top section: Code selectors */}
                                            <div className="bg-light p-3 rounded">
                                                <div className="row mb-2">
                                                    <CompFmtSelectbox
                                                        attrs={formObj.comptnCd}
                                                        grpCd={"COMPT"}
                                                        selectType={"S"}
                                                        onChange={onComptnCdChange}
                                                    />
                                                </div>
                                                <div className="row">
                                                    <CompFmtSelectbox
                                                        attrs={formObj.dvsnCd}
                                                        grpCd={"DVSNCD"}
                                                        selectType={"S"}
                                                        customParams={dvsnParams}
                                                        onChange={onChangeMtmkg}
                                                    />
                                                    <CompFmtSelectbox
                                                        attrs={formObj.wetclsCd}
                                                        grpCd={"WETCLSCD"}
                                                        selectType={"S"}
                                                        customParams={wetclsParams}
                                                        onChange={onChangeMtmkg}
                                                    />
                                                    <CompFmtSelectbox
                                                        attrs={formObj.armType}
                                                        grpCd={"GRP005"}
                                                        selectType={"S"}
                                                        onChange={onInputChange}
                                                    />
                                                </div>
                                            </div>

                                            {/* Player and result information */}
                                            <div className="p-3 rounded" style={{marginTop: '0px'}}>
                                                <div className="row">
                                                    <CompFmtInput
                                                        attrs={formObj.plyrCd}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                    <CompFmtInput
                                                        attrs={formObj.rnk}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer flex-column align-items-stretch w-100 gap-2 pb-3 border-top-0">
                            <div className="btn-group">
                                <button type="button" className="btn btn-lg btn-primary" onClick={onSave}>
                                    Save changes
                                </button>
                                <button type="button" className="btn btn-lg btn-secondary" data-bs-dismiss="modal" onClick={handleToggle}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
});

export default CompFormMatchResult;